/**
 * DCTA-162 — the bank's voice, by reference: the one seam between the app and the speech route (backend DCTA-161, not
 * built yet; its contract is proposed in docs/journal/DCTA-162.md). Wiring the real route is a change to this file only.
 *
 * The app never sends a word to be spoken. It names an object the bank emitted — a question, a refusal, a reply, a card
 * to sign (its read-back), a card's end — and the bank writes every sentence from that object (Claude's ruling for
 * DCTA-161; docs/design/ux-copy/spoken.md §0): the figures in words, the never-spoken list left out (§1.3), the
 * audit's sha256 over what is said. The app gets the number of sentences back, then each sentence's audio, and plays
 * them in order (`app/read-aloud.ts`). Nothing to say, the day's cap, a failure, an answer that is not audio: silence.
 * Speech carries no authority: nothing here can sign, answer or advance anything.
 */

export type SpeechPart = "question" | "refusal" | "reply" | "readback" | "end";

/** An object the bank emitted, by its own ids — the request's whole body. */
export interface SpeechRef {
  readonly part: SpeechPart;
  readonly draft_id?: string;
  readonly bundle_id?: string;
  /** Which question of the draft (the one on the screen). */
  readonly question_id?: string;
  /** Which refused step of the draft. */
  readonly step_id?: string;
  /** An answer that came as an error has no draft to name — the freeze (11q): its audit record does. */
  readonly audit_ref?: string;
  /**
   * The app's own fixed lines, by row (19j could-not-hear, 3e the give-up; DCTA-199 LN3 `token`, "Enter your code." when the
   * token code is due): the bank holds their words.
   */
  readonly line?: "19j" | "3e" | "token";
  /** A read answer (§1.4): the bank's pointer to the screen for that read ("Your balances are on the screen.") — never a figure. */
  readonly read?: string;
}

/** The bank's answer to a reference: how many sentences, and the handle to fetch each one's audio by. */
export interface SpeechPlan {
  readonly id: string;
  readonly count: number;
}

export interface SpeechSource {
  /** The bank's sentences for that object; `undefined` = silence (nothing to say, the cap, a failure). Never rejects. */
  plan(ref: SpeechRef, signal: AbortSignal): Promise<SpeechPlan | undefined>;
  /** Sentence `n`'s audio; `undefined` = silence from here. Never rejects. */
  audio(plan: SpeechPlan, n: number, signal: AbortSignal): Promise<ArrayBuffer | undefined>;
}

const REF_KEYS = ["part", "draft_id", "bundle_id", "question_id", "step_id", "audit_ref", "line", "read"] as const;
/** The id goes into the audio's path: plain characters only. */
const SPEECH_ID = /^[A-Za-z0-9_-]{1,64}$/;
/** A five-step read-back is about nine sentences (spoken.md §5 G-R5); a plan above this is not one. */
const MAX_SEGMENTS = 24;

// ---------------------------------------------------------------- the bank's route

/**
 * The route proposed to DCTA-161: `POST /v1/speech` with the reference → `{speech_id, segments}`; `GET
 * /v1/speech/{speech_id}/{n}` → sentence n's audio (`audio/*`). Same origin, the session cookie, the CSRF header on
 * the POST, never cached. The large-type rate is the bank's (it knows the profile); nothing about the voice is asked.
 */
export function routeSpeech(o: { readonly csrf: () => string; readonly fetcher?: typeof fetch }): SpeechSource {
  const fetcher = o.fetcher ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
  return {
    async plan(ref, signal) {
      const body: Record<string, string> = {};
      for (const key of REF_KEYS) {
        const value = ref[key];
        if (typeof value === "string" && value !== "") body[key] = value;
      }
      try {
        const response = await fetcher("/v1/speech", {
          method: "POST",
          headers: { "content-type": "application/json", "x-dcta-csrf": o.csrf() },
          credentials: "same-origin",
          cache: "no-store",
          body: JSON.stringify(body),
          signal,
        });
        if (response.status !== 200) return undefined;
        return planOf(await response.json());
      } catch {
        return undefined;
      }
    },
    async audio(plan, n, signal) {
      try {
        const response = await fetcher(`/v1/speech/${plan.id}/${String(n)}`, { method: "GET", credentials: "same-origin", cache: "no-store", signal });
        if (response.status !== 200 || !(response.headers.get("content-type") ?? "").startsWith("audio/")) return undefined;
        const audio = await response.arrayBuffer();
        return audio.byteLength === 0 ? undefined : audio;
      } catch {
        return undefined;
      }
    },
  };
}

/** Checked field by field: an answer that is not a plan says nothing. */
function planOf(body: unknown): SpeechPlan | undefined {
  if (typeof body !== "object" || body === null) return undefined;
  const id: unknown = Reflect.get(body, "speech_id");
  const count: unknown = Reflect.get(body, "segments");
  if (typeof id !== "string" || !SPEECH_ID.test(id)) return undefined;
  if (typeof count !== "number" || !Number.isInteger(count) || count < 1 || count > MAX_SEGMENTS) return undefined;
  return { id, count };
}

// ---------------------------------------------------------------- the stand-in

/** How many sentences the stand-in gives each part: one line per object (DCTA-199, docs/design/v3/spoken-cta.md B64). */
const FAKE_COUNT: Readonly<Record<SpeechPart, number>> = { question: 1, refusal: 1, reply: 1, readback: 1, end: 1 };

/**
 * The stand-in until DCTA-161 lands (`?speech=fake`): the route's shape, silent sentences — never words, never a
 * figure, so it can never be mistaken for the bank's voice. It exercises the playback (order, gaps, Stop, barge-in).
 */
export function fakeSpeech(o: { readonly ms?: number } = {}): SpeechSource {
  const ms = o.ms ?? 900;
  return {
    async plan(ref, signal) {
      await Promise.resolve();
      return signal.aborted ? undefined : { id: `fake-${ref.part}`, count: FAKE_COUNT[ref.part] };
    },
    async audio(_plan, _n, signal) {
      await Promise.resolve();
      return signal.aborted ? undefined : silentWav(ms);
    },
  };
}

/** `ms` of silence as a 16-bit mono 8 kHz WAV. */
export function silentWav(ms: number): ArrayBuffer {
  const rate = 8000;
  const data = Math.max(0, Math.round((rate * ms) / 1000)) * 2;
  const buffer = new ArrayBuffer(44 + data);
  const view = new DataView(buffer);
  const ascii = (at: number, text: string): void => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(at + i, text.charCodeAt(i));
  };
  ascii(0, "RIFF");
  view.setUint32(4, 36 + data, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  ascii(36, "data");
  view.setUint32(40, data, true);
  return buffer;
}

/** `?speech=fake` for the stand-in; anything else is the bank's route (silent until DCTA-161 answers). */
export function speechFlag(search: string): "fake" | "route" {
  return new URLSearchParams(search).get("speech") === "fake" ? "fake" : "route";
}
